import { Box, Card, HStack, IconButton, Text } from '@chakra-ui/react'
import { Pencil, Trash2 } from 'lucide-react'
import React from 'react'
import { Link } from 'react-router'

import type { SwitchboardTranslateFn } from '../../types'
import { formatSwitchboardTimestamp } from '../../utils/formatting'
import { text } from '../../utils/text'

export interface SwitchboardCardContentProps {
  /** Display name of the switchboard */
  displayName: string
  /** Last updated date */
  lastUpdated: Date
  /** Extension dial plan ID for navigation */
  extensionDialPlan: number
  /** Callback when edit button is clicked */
  onEdit: () => void
  /** Callback when delete button is clicked */
  onDelete?: () => void
  /** Whether user can delete switchboards */
  canDelete?: boolean
  /** Whether delete operation is in progress */
  isDeleting: boolean
  /** Callback when card title is clicked (for navigation) */
  onNavigate?: () => void
  /** Route used for native browser link navigation */
  navigationHref?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

/**
 * Card content component displaying switchboard info with edit/delete actions
 */
export const SwitchboardCardContent = React.memo<SwitchboardCardContentProps>(
  ({
    displayName,
    lastUpdated,
    onEdit,
    onDelete,
    canDelete = true,
    isDeleting,
    onNavigate,
    navigationHref,
    t,
  }) => {
    const title = (
      <Text
        fontWeight="semibold"
        fontSize="md"
        color="fg.default"
        _hover={
          onNavigate || navigationHref ? { color: 'brand.solid' } : undefined
        }
        cursor={onNavigate || navigationHref ? 'pointer' : 'default'}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {displayName ||
          text(
            t,
            'switchboard.list.common.unnamedSwitchboard',
            'Unnamed Switchboard',
          )}
      </Text>
    )

    return (
      <Card.Root
        p={4}
        gap={6}
        bg="bg.default"
        boxShadow="card"
        borderRadius="8px"
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          w="100%"
        >
          {navigationHref ? (
            <Box asChild flex="0 0 66.67%" minWidth={0} textDecoration="none">
              <Link to={navigationHref}>{title}</Link>
            </Box>
          ) : (
            <Box
              flex="0 0 66.67%"
              minWidth={0}
              cursor={onNavigate ? 'pointer' : 'default'}
              onClick={onNavigate}
            >
              {title}
            </Box>
          )}

          <HStack gap={3}>
            <IconButton
              size="sm"
              variant="subtle"
              colorPalette="neutral"
              borderRadius="sm"
              onClick={onEdit}
              aria-label={text(
                t,
                'switchboard.list.card.editAria',
                'Edit switchboard',
              )}
              css={{ '& svg': { stroke: 'var(--chakra-colors-gray-fg)' } }}
            >
              <Pencil size={16} />
            </IconButton>
            {canDelete && onDelete && (
              <IconButton
                size="sm"
                variant="subtle"
                colorPalette="error"
                borderRadius="sm"
                onClick={onDelete}
                loading={isDeleting}
                aria-label={text(
                  t,
                  'switchboard.list.card.deleteAria',
                  'Delete',
                )}
                css={{ '& svg': { stroke: 'var(--chakra-colors-red-fg)' } }}
              >
                <Trash2 size={18} />
              </IconButton>
            )}
          </HStack>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          w="100%"
        >
          <Text fontSize="sm" color="fg.subtle">
            {text(t, 'switchboard.list.card.lastUpdated', 'Last Updated')}
          </Text>
          <Text fontSize="sm" color="fg.subtle">
            {formatSwitchboardTimestamp(lastUpdated)}
          </Text>
        </Box>
      </Card.Root>
    )
  },
)

SwitchboardCardContent.displayName = 'SwitchboardCardContent'
